import type { RuleSet } from '../rules';
import { fromMilli, type PlayerId, type SimState, spyView, toMilli, type Unit } from '../sim';

/**
 * The state as `viewer` may see it, as far as spies go (sim spies.ts
 * `spyView`): a rival's hidden spy isn't in it at all, and a rival spy
 * disguised in one of the viewer's cities is one of the viewer's own units
 * of its disguise type (full hp, carrying nothing, automated when that type
 * starts automated, so it reads like the city's other units), so
 * every panel, layer and notification built on it shows what the victim is
 * meant to see and nothing more. Everything else is the real state (the
 * same objects). Commands a fake unit is given reach the sim, which ignores
 * them: it isn't really theirs. Vision must still come from the real state
 * (a fake unit sees nothing; a spy's owner sees its city).
 *
 * Returns `state` itself when no rival spy needs hiding or disguising, and
 * caches the last view per (state, tick) since the host mutates its state in
 * place and layers read it every frame.
 */
let cache: { state: SimState; tick: number; viewer: PlayerId; view: SimState } | null = null;

export function viewerState(state: SimState, rules: RuleSet, viewer: PlayerId): SimState {
  if (cache && cache.state === state && cache.tick === state.tick && cache.viewer === viewer) {
    return cache.view;
  }
  let units: Record<string, Unit> | null = null;
  for (const [id, unit] of Object.entries(state.units)) {
    if (unit.owner === viewer || !rules.units[unit.type]?.stealth) continue;
    const view = spyView(state, rules, unit, viewer);
    if (view.kind === 'own' || view.kind === 'visible') continue;
    units ??= { ...state.units };
    if (view.kind === 'hidden') {
      delete units[id];
      continue;
    }
    units[id] = {
      id,
      owner: viewer,
      type: view.as,
      pos: { ...unit.pos },
      hp: toMilli(rules.units[view.as]?.hp ?? fromMilli(unit.hp)),
      progress: 0,
      // An automated type looks automated: a manual one would stand out in the
      // roster and offer an Auto button that does nothing. A worker's last
      // search found nothing, so standing still it reads "no work found" like
      // a real worker with no free work (not "idle", which no auto worker
      // shows); it's in the past, so it raises no idle-workers notification.
      ...(rules.units[view.as]?.automate
        ? {
            auto: rules.units[view.as]?.automate === 'work' ? { retryTick: state.tick - 1 } : {},
          }
        : {}),
      ...(unit.disguise ? { homeCityId: unit.disguise.cityId } : {}),
    };
  }
  const view = units ? { ...state, units } : state;
  cache = { state, tick: state.tick, viewer, view };
  return view;
}

/**
 * Whether `unitId` is one of `viewer`'s own units in the real state; a unit
 * they see as theirs that isn't is a disguised spy (see `viewerState`).
 */
export function reallyOwns(state: SimState, viewer: PlayerId, unitId: string): boolean {
  return state.units[unitId]?.owner === viewer;
}
